import { Message, UploadFile, Popover, Select as FaveSelect } from 'fave-ui';
import { useFormikContext } from 'formik';
import { CaretRight, Eye, EyeSlash } from 'phosphor-react';
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import { useLocation, useParams } from 'react-router';
import { Input } from '../../../components/common/form/Input';
import SubmitButton from '../../../components/common/form/SubmitButton';
import {
  showRemoveFileModal,
  showUnselectPaymentModeModal,
} from '../../../helpers/formHelpers';
import {
  PaymentModeFormType,
  PaymentModeType,
  useDeletePaymentModeDocument,
} from '../../../services/PaymentMode/usePaymentMode';
import { McApprovalDocumentsType } from '../../../types/dataTypes';
import { filterObjectByKey } from '../../../utils/utilFunctions';
import {
  approvalDocumentsFieldKey,
  outletsFieldKey,
  isPaynow as isPaynowGroup,
  PAYNOW_VARIANT,
  filterOutletOption,
} from '../helpers/paymentModeHelpers';
import style from '../style.module.css';
import { Select } from '../../../components/common/form';
import { ActionForm, OutletStatusPaymentMode } from '../helpers/enums';
import { useTenantContext } from '../../../contexts/TenantContext';

type PaymentModeCreateFormProps = {
  isApplicationFeePage?: boolean;
  enableUpdateForm?: boolean;
  listOutlets?: {
    key: string;
    label: string;
    value: string;
    has_draft_payment_mode: boolean;
    has_existing_payment_mode: boolean;
    id: number;
  }[];
  setStatusOutletPaymentMode?: React.Dispatch<
    React.SetStateAction<OutletStatusPaymentMode>
  >;
};

const PasswordField = ({
  name,
  value,
  valuePath,
  disabled,
}: {
  name: string;
  value: string;
  valuePath: string;
  disabled?: boolean;
}) => {
  const [visible, setVisible] = useState(false);
  return (
    <Input
      name={name}
      type={visible ? 'text' : 'password'}
      value={value}
      placeholder="password"
      allowClear
      bordered={false}
      valuePath={valuePath}
      disabled={disabled}
      addonAfter={
        <button
          type="button"
          onClick={() => setVisible(v => !v)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            display: 'flex',
            alignItems: 'center',
          }}
          disabled={disabled}
          tabIndex={-1}
        >
          {visible ? <EyeSlash size={16} /> : <Eye size={16} />}
        </button>
      }
    />
  );
};

const GroupedInputBoxes = ({
  title,
  paymentModes,
  isFastpay,
  showUncheckModal,
  disabled,
  currency,
}: {
  title: string;
  paymentModes: PaymentModeType[];
  isFastpay: boolean;
  showUncheckModal?: boolean;
  disabled?: boolean;
  currency?: string;
}) => {
  const { setFieldValue, values } = useFormikContext<PaymentModeFormType>();
  const paynowValueCacheRef = useRef<
    Record<string, number | string | undefined | null>
  >({});
  const CLEARED_SENTINEL = null;
  const paynowOptions = useMemo(
    () =>
      paymentModes
        .filter(pm => isPaynowGroup(pm.group))
        .map(pm => ({ label: pm.variant, value: pm.variant })),
    [paymentModes],
  );
  const paynowVariants = useMemo(
    () => paymentModes.filter(pm => isPaynowGroup(pm.group)),
    [paymentModes],
  );
  const paynowVariantWithData = useMemo(
    () =>
      paymentModes
        .filter(pm => isPaynowGroup(pm.group))
        .find(pm => {
          const rateKey = `rate${pm.group.replace(' ', '')}${pm.variant}`;
          const rateValue = pm[rateKey];
          return (
            rateValue !== undefined && rateValue !== null && rateValue !== ''
          );
        }),
    [paymentModes],
  );
  const defaultPaynowOption = useMemo(
    () =>
      (paynowVariantWithData && {
        label: paynowVariantWithData.variant,
        value: paynowVariantWithData.variant,
      }) ||
      paynowOptions.find(option => option.value === PAYNOW_VARIANT.IN_HOUSE) ||
      paynowOptions[0],
    [paynowOptions, paynowVariantWithData],
  );
  const hasUserSelectedPaynowRef = useRef(false);
  const previousSelectedPaynowRef = useRef<string | undefined>(
    defaultPaynowOption?.value,
  );
  const [selectedPaynow, setSelectedPaynow] = useState(
    defaultPaynowOption?.value,
  );

  useEffect(() => {
    const isSelectedValid = paynowOptions.some(
      option => option.value === selectedPaynow,
    );
    const preferredValue =
      paynowVariantWithData?.variant || defaultPaynowOption?.value;
    const shouldUpdateSelection =
      (!isSelectedValid && preferredValue) ||
      (!hasUserSelectedPaynowRef.current &&
        preferredValue &&
        selectedPaynow !== preferredValue &&
        !!paynowVariantWithData);

    if (shouldUpdateSelection && preferredValue) {
      setSelectedPaynow(preferredValue);
    }
  }, [
    defaultPaynowOption,
    paynowOptions,
    paynowVariantWithData,
    selectedPaynow,
  ]);

  const handleChangePaynow = useCallback(
    (value: string) => {
      const newVariant = value as string;
      if (selectedPaynow !== newVariant) {
        const previous = paymentModes.find(
          item => item.variant === selectedPaynow,
        );
        const prevPaymentModeId = previous?.payment_mode?.id || previous?.id;
        const removedIds = values.removed_payment_mode_ids || [];

        if (prevPaymentModeId && !removedIds.includes(prevPaymentModeId)) {
          setFieldValue('removed_payment_mode_ids', [
            ...removedIds,
            prevPaymentModeId,
          ]);
        }
      }

      hasUserSelectedPaynowRef.current = true;
      setSelectedPaynow(newVariant);
    },
    [
      paymentModes,
      selectedPaynow,
      setFieldValue,
      values.removed_payment_mode_ids,
    ],
  );

  const syncPaynowValues = useCallback(() => {
    if (!paynowVariants.length || !selectedPaynow) return;

    const isSwitchingVariants =
      previousSelectedPaynowRef.current !== selectedPaynow;
    if (isSwitchingVariants) {
      previousSelectedPaynowRef.current = selectedPaynow;
    }

    paynowVariants.forEach((pm, idx) => {
      const rateKey = `rate${pm.group.replace(' ', '')}${pm.variant}`;
      const currentValue = pm[rateKey];
      const formFieldPath = `${title}.${idx}.${rateKey}`;
      const actualFormValue = values[title]?.[idx]?.[rateKey];
      const cachedValue = paynowValueCacheRef.current[pm.variant];

      if (pm.variant === selectedPaynow) {
        const valueToCheck =
          actualFormValue !== undefined ? actualFormValue : currentValue;
        const isEmpty =
          valueToCheck === undefined ||
          valueToCheck === null ||
          valueToCheck === '';

        if (isEmpty) {
          // Only restore from cache if we're switching to this variant and cache is not cleared
          // If we're already on this variant and field is empty, user cleared it
          if (
            isSwitchingVariants &&
            cachedValue !== undefined &&
            cachedValue !== '' &&
            cachedValue !== CLEARED_SENTINEL
          ) {
            setFieldValue(formFieldPath, cachedValue);
            paynowValueCacheRef.current[pm.variant] = cachedValue;
          } else if (!isSwitchingVariants) {
            paynowValueCacheRef.current[pm.variant] = CLEARED_SENTINEL;
            setFieldValue(formFieldPath, undefined);

            const paymentModeId = pm.payment_mode?.id || pm.id;
            if (paymentModeId) {
              const removedIds = values.removed_payment_mode_ids || [];
              if (!removedIds.includes(paymentModeId)) {
                setFieldValue('removed_payment_mode_ids', [
                  ...removedIds,
                  paymentModeId,
                ]);
              }
            }
          }
        } else {
          const previousCache = paynowValueCacheRef.current[pm.variant];
          if (previousCache !== CLEARED_SENTINEL) {
            const valueToSet = (
              actualFormValue !== undefined ? actualFormValue : currentValue
            ) as number | string;
            paynowValueCacheRef.current[pm.variant] = valueToSet;

            if (previousCache === CLEARED_SENTINEL) {
              const paymentModeId = pm.payment_mode?.id || pm.id;
              if (paymentModeId) {
                const removedIds = values.removed_payment_mode_ids || [];
                const updatedRemovedIds = removedIds.filter(
                  id => id !== paymentModeId,
                );
                if (updatedRemovedIds.length !== removedIds.length) {
                  setFieldValue(
                    'removed_payment_mode_ids',
                    updatedRemovedIds.length > 0
                      ? updatedRemovedIds
                      : undefined,
                  );
                }
              }
            }

            if (isSwitchingVariants || previousCache !== valueToSet) {
              setFieldValue(formFieldPath, valueToSet);
            }
          }
        }
      } else {
        const hasValue =
          currentValue !== undefined &&
          currentValue !== null &&
          currentValue !== '';
        if (hasValue) {
          paynowValueCacheRef.current[pm.variant] = currentValue as
            | number
            | string;
          setFieldValue(`${title}.${idx}.${rateKey}`, undefined);
        }
      }
    });
  }, [paynowVariants, selectedPaynow, setFieldValue, title]);

  useEffect(() => {
    paynowVariants.forEach(pm => {
      const rateKey = `rate${pm.group.replace(' ', '')}${pm.variant}`;
      const rateValue = pm[rateKey];
      const hasValue =
        rateValue !== undefined && rateValue !== null && rateValue !== '';
      const currentCache = paynowValueCacheRef.current[pm.variant];
      if (hasValue && currentCache !== CLEARED_SENTINEL) {
        paynowValueCacheRef.current[pm.variant] = rateValue as number | string;
      }
    });
    syncPaynowValues();
  }, [paynowVariants, syncPaynowValues]);

  return (
    <tr className={style.groupedInputBox}>
      <td className={style.group}>
        {title}
        {paymentModes[0].is_ipp === true ? (
          <div>
            <p style={{ color: '#FF0000', fontSize: '0.8rem' }}>
              **Required when merchant opt-in for IPP
            </p>
          </div>
        ) : null}
        <div className={style.greyLine} />
      </td>
      {paymentModes.map((paymentMode, index) => {
        const objectPath = `${title}.${index}`;
        const isIpp = paymentMode.is_ipp;
        const isGpl = paymentMode.is_gpl;
        const isGplViaPos = paymentMode.is_gpl_via_pos;
        const isPaynow = isPaynowGroup(paymentMode.group);
        const showCheckbox = isFastpay || (showUncheckModal && isIpp);

        // grabpay later has a custom design, so we need to separate it from the rest
        if (isGpl) {
          const keySuffix = `${paymentMode.group.replace(' ', '')}${paymentMode.variant}`;
          const gplRateFieldValue = paymentMode[`rate${keySuffix}`];
          const minimumSpendAmountValue =
            paymentMode[`minimum_spend_amount${keySuffix}`];
          const hasMinimumSpendAmount =
            minimumSpendAmountValue !== undefined &&
            minimumSpendAmountValue !== null &&
            minimumSpendAmountValue !== '';
          const shouldDisableField =
            disabled || !gplRateFieldValue || gplRateFieldValue === '0';

          return (
            <>
              {/* PayLater by Grab rate  */}
              <td key={objectPath} className={style.variant}>
                <div className={style.checkbox}>
                  <span className={style.title}>{paymentMode.variant}</span>
                </div>
                <div className={style.input}>
                  <>
                    <div className={style.inputBox}>
                      <Input
                        name={`rate${keySuffix}`}
                        type={'number'}
                        value={paymentMode[`rate${keySuffix}`] as number}
                        addonAfter={'%'}
                        placeholder="5"
                        allowClear
                        bordered={false}
                        valuePath={objectPath}
                        disabled={disabled}
                        onValueChange={
                          isGplViaPos
                            ? val => {
                                if (!val) {
                                  setFieldValue(
                                    `${objectPath}.minimum_spend_amount${keySuffix}`,
                                    undefined,
                                  );
                                  setFieldValue(
                                    `${objectPath}.store_grab_id${keySuffix}`,
                                    undefined,
                                  );
                                  setFieldValue(
                                    `${objectPath}.terminal_id${keySuffix}`,
                                    undefined,
                                  );
                                }
                              }
                            : undefined
                        }
                      />
                    </div>
                  </>
                </div>
              </td>
              {/* Minimum spend amount */}
              <td key={`${objectPath}-1`} className={style.variant}>
                <div className={style.checkbox}>
                  <span className={style.title}>Minimum amount</span>
                </div>
                <div className={style.input}>
                  <div className={style.inputBox}>
                    <Input
                      name={`minimum_spend_amount${keySuffix}`}
                      type={'number'}
                      value={
                        paymentMode[
                          `minimum_spend_amount${keySuffix}`
                        ] as number
                      }
                      addonBefore={currency}
                      placeholder="5"
                      allowClear
                      bordered={false}
                      valuePath={objectPath}
                      disabled={shouldDisableField}
                      onValueChange={
                        isGplViaPos
                          ? val => {
                              if (!val) {
                                setFieldValue(
                                  `${objectPath}.store_grab_id${keySuffix}`,
                                  undefined,
                                );
                                setFieldValue(
                                  `${objectPath}.terminal_id${keySuffix}`,
                                  undefined,
                                );
                              }
                            }
                          : undefined
                      }
                    />
                  </div>
                </div>
              </td>
              {isGplViaPos && (
                <td
                  key={`${objectPath}-store-grab-id`}
                  className={style.variant}
                >
                  <div className={style.checkbox}>
                    <span className={style.title}>Store Grab ID</span>
                  </div>
                  <div className={style.input}>
                    <div className={style.inputBox}>
                      <Input
                        name={`store_grab_id${keySuffix}`}
                        type={'text'}
                        value={
                          paymentMode[`store_grab_id${keySuffix}`] as string
                        }
                        placeholder="store grab id"
                        allowClear
                        bordered={false}
                        valuePath={objectPath}
                        disabled={shouldDisableField || !hasMinimumSpendAmount}
                      />
                    </div>
                  </div>
                </td>
              )}
              {isGplViaPos && (
                <td key={`${objectPath}-terminal-id`} className={style.variant}>
                  <div className={style.checkbox}>
                    <span className={style.title}>Terminal ID</span>
                  </div>
                  <div className={style.input}>
                    <div className={style.inputBox}>
                      <Input
                        name={`terminal_id${keySuffix}`}
                        type={'text'}
                        value={paymentMode[`terminal_id${keySuffix}`] as string}
                        placeholder="terminal id"
                        allowClear
                        bordered={false}
                        valuePath={objectPath}
                        disabled={shouldDisableField || !hasMinimumSpendAmount}
                      />
                    </div>
                  </div>
                </td>
              )}
              {!isGplViaPos && (
                <>
                  {/* Cashback opt out */}
                  <td key={`${objectPath}-2`} className={style.variant}>
                    <div className={style.checkbox}>
                      <span className={style.title}>Cashback opt out</span>
                    </div>
                    <div className={style.input}>
                      {paymentMode.status && (
                        <div className={style.inputBox}>
                          <Input
                            name={`is_cashback_opt_out${keySuffix}`}
                            type={'checkbox'}
                            checked={
                              paymentMode[
                                `is_cashback_opt_out${keySuffix}`
                              ] as boolean
                            }
                            valuePath={objectPath}
                            disabled={shouldDisableField}
                            onChangeCheckbox={(isChecked, handleChange) => {
                              const showUntickPaymentModeModal =
                                showUncheckModal && !isChecked && isFastpay;

                              showUntickPaymentModeModal
                                ? showUnselectPaymentModeModal({
                                    onOk: handleChange,
                                  })
                                : handleChange();
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </td>
                </>
              )}
            </>
          );
        } else if (paymentMode.is_atome) {
          const keySuffix = `${paymentMode.group.replace(' ', '')}${paymentMode.variant}`;
          const atomeRateFieldValue = paymentMode[`rate${keySuffix}`];
          const shouldDisableField =
            disabled || !atomeRateFieldValue || atomeRateFieldValue === '0';

          return (
            <>
              {/* Atome rate  */}
              <td key={objectPath} className={style.variant}>
                <div className={style.checkbox}>
                  <span className={style.title}>{paymentMode.variant}</span>
                </div>
                <div className={style.input}>
                  <>
                    <div className={style.inputBox}>
                      <Input
                        name={`rate${keySuffix}`}
                        type={'number'}
                        value={paymentMode[`rate${keySuffix}`] as number}
                        addonAfter={'%'}
                        placeholder="5"
                        allowClear
                        bordered={false}
                        valuePath={objectPath}
                        disabled={disabled}
                        onValueChange={val => {
                          if (!val) {
                            setFieldValue(
                              `${objectPath}.username${keySuffix}`,
                              undefined,
                            );
                            setFieldValue(
                              `${objectPath}.password${keySuffix}`,
                              undefined,
                            );
                          }
                        }}
                      />
                    </div>
                  </>
                </div>
              </td>
              {/* Username */}
              <td key={`${objectPath}-username`} className={style.variant}>
                <div className={style.checkbox}>
                  <span className={style.title}>Username</span>
                </div>
                <div className={style.input}>
                  <div className={style.inputBox}>
                    <Input
                      name={`username${keySuffix}`}
                      type={'text'}
                      value={paymentMode[`username${keySuffix}`] as string}
                      placeholder="username"
                      allowClear
                      bordered={false}
                      valuePath={objectPath}
                      disabled={shouldDisableField}
                    />
                  </div>
                </div>
              </td>
              {/* Password */}
              <td key={`${objectPath}-password`} className={style.variant}>
                <div className={style.checkbox}>
                  <span className={style.title}>Password</span>
                </div>
                <div className={style.input}>
                  <div className={style.inputBox}>
                    <PasswordField
                      name={`password${keySuffix}`}
                      value={paymentMode[`password${keySuffix}`] as string}
                      valuePath={objectPath}
                      disabled={shouldDisableField}
                    />
                  </div>
                </div>
              </td>
            </>
          );
        } else if (paymentMode.is_shopee_pay) {
          const keySuffix = `${paymentMode.group.replace(' ', '')}${paymentMode.variant}`;
          const shopeePayRateFieldValue = paymentMode[`rate${keySuffix}`];
          const shouldDisableField =
            disabled ||
            !shopeePayRateFieldValue ||
            shopeePayRateFieldValue === '0';

          return (
            <>
              {/* ShopeePay rate */}
              <td key={objectPath} className={style.variant}>
                <div className={style.checkbox}>
                  <span className={style.title}>{paymentMode.variant}</span>
                </div>
                <div className={style.input}>
                  <div className={style.inputBox}>
                    <Input
                      name={`rate${keySuffix}`}
                      type={'number'}
                      value={paymentMode[`rate${keySuffix}`] as number}
                      addonAfter={'%'}
                      placeholder="5"
                      allowClear
                      bordered={false}
                      valuePath={objectPath}
                      disabled={disabled}
                      onValueChange={val => {
                        if (!val) {
                          setFieldValue(
                            `${objectPath}.merchant_id${keySuffix}`,
                            undefined,
                          );
                          setFieldValue(
                            `${objectPath}.store_id${keySuffix}`,
                            undefined,
                          );
                        }
                      }}
                    />
                  </div>
                </div>
              </td>
              {/* Merchant ID */}
              <td key={`${objectPath}-merchant-id`} className={style.variant}>
                <div className={style.checkbox}>
                  <span className={style.title}>Merchant ID</span>
                </div>
                <div className={style.input}>
                  <div className={style.inputBox}>
                    <Input
                      name={`merchant_id${keySuffix}`}
                      type={'text'}
                      value={paymentMode[`merchant_id${keySuffix}`] as string}
                      placeholder="merchant id"
                      allowClear
                      bordered={false}
                      valuePath={objectPath}
                      disabled={shouldDisableField}
                    />
                  </div>
                </div>
              </td>
              {/* Store ID */}
              <td key={`${objectPath}-store-id`} className={style.variant}>
                <div className={style.checkbox}>
                  <span className={style.title}>Store ID</span>
                </div>
                <div className={style.input}>
                  <div className={style.inputBox}>
                    <Input
                      name={`store_id${keySuffix}`}
                      type={'text'}
                      value={paymentMode[`store_id${keySuffix}`] as string}
                      placeholder="store id"
                      allowClear
                      bordered={false}
                      valuePath={objectPath}
                      disabled={shouldDisableField}
                    />
                  </div>
                </div>
              </td>
            </>
          );
        } else if (isPaynow) {
          // only render a single select/input for PayNow variants
          if (index !== 0 || !paynowOptions.length) return null;
          const selectedPaynowPaymentMode =
            paymentModes.find(item => item.variant === selectedPaynow) ||
            paymentModes[0];
          const selectedPaynowIndex = paymentModes.findIndex(
            item => item.variant === selectedPaynowPaymentMode.variant,
          );
          const paynowValuePath = `${title}.${
            selectedPaynowIndex > -1 ? selectedPaynowIndex : 0
          }`;

          return (
            <td key={objectPath} className={style.variant}>
              <div className={style.checkbox}>
                <FaveSelect
                  value={selectedPaynow}
                  defaultValue={defaultPaynowOption?.value}
                  options={paynowOptions}
                  onChange={value => handleChangePaynow(value as string)}
                  className={style.paynowSelect}
                  disabled={disabled}
                />
              </div>
              <div className={style.input}>
                <div className={style.inputBox}>
                  <Popover
                    title="PayNow Registration"
                    content="If this field is blank, outlet is not registered for PayNow"
                    trigger="hover"
                    placement="top"
                  >
                    <Input
                      name={`rate${selectedPaynowPaymentMode.group.replace(' ', '')}${selectedPaynowPaymentMode.variant}`}
                      type={'number'}
                      value={
                        selectedPaynowPaymentMode[
                          `rate${selectedPaynowPaymentMode.group.replace(' ', '')}${selectedPaynowPaymentMode.variant}`
                        ] as number
                      }
                      addonAfter={'%'}
                      placeholder="5"
                      allowClear
                      bordered={false}
                      valuePath={paynowValuePath}
                      disabled={disabled}
                    />
                  </Popover>
                </div>
              </div>
            </td>
          );
        } else {
          return (
            <td key={objectPath} className={style.variant}>
              <div className={style.checkbox}>
                {showCheckbox && (
                  <Input
                    disabled={disabled}
                    name={'status'}
                    type={'checkbox'}
                    checked={paymentMode.status as boolean}
                    valuePath={objectPath}
                    onChangeCheckbox={(isChecked, handleChange) => {
                      const showUntickPaymentModeModal =
                        showUncheckModal && !isChecked && isFastpay;

                      showUntickPaymentModeModal
                        ? showUnselectPaymentModeModal({
                            onOk: handleChange,
                          })
                        : handleChange();
                    }}
                  />
                )}
                <span className={style.title}>{paymentMode.variant}</span>
              </div>
              <div className={style.input}>
                {(paymentMode.status || !isFastpay) && (
                  <>
                    {paymentMode.is_fpx && (
                      <div className={style.inputBox}>
                        <Input
                          name={
                            'fixed_amount' +
                            `${paymentMode.group.replace(' ', '')}${
                              paymentMode.variant
                            }`
                          }
                          type={'number'}
                          value={
                            paymentMode[
                              `fixed_amount${paymentMode.group.replace(
                                ' ',
                                '',
                              )}${paymentMode.variant}`
                            ] as number
                          }
                          addonBefore={'RM'}
                          placeholder="5"
                          allowClear
                          bordered={false}
                          valuePath={objectPath}
                          disabled={disabled}
                        />
                      </div>
                    )}
                    <div className={style.inputBox}>
                      <Input
                        name={
                          'rate' +
                          `${paymentMode.group.replace(' ', '')}${
                            paymentMode.variant
                          }`
                        }
                        type={'number'}
                        value={
                          paymentMode[
                            `rate${paymentMode.group.replace(' ', '')}${
                              paymentMode.variant
                            }`
                          ] as number
                        }
                        addonAfter={'%'}
                        placeholder="5"
                        allowClear
                        bordered={false}
                        valuePath={objectPath}
                        disabled={disabled}
                      />
                    </div>
                  </>
                )}
              </div>
            </td>
          );
        }
      })}
    </tr>
  );
};

const PaymentModeCreateForm: React.FC<PaymentModeCreateFormProps> = ({
  isApplicationFeePage = true,
  enableUpdateForm = true,
  listOutlets = [],
  setStatusOutletPaymentMode = () => {},
}) => {
  const { id, mode } = useParams();
  const navigate = useLocation();
  const { tenantCurrency } = useTenantContext();
  const params = new URLSearchParams(navigate.search);
  const parsedID = parseInt(id || '');

  const isCreateMode = mode === ActionForm.Create;
  const isResubmit = mode === ActionForm.Resubmit;

  const { values } = useFormikContext<PaymentModeFormType>();
  const { mutate: deletePaymentModeDocument } = useDeletePaymentModeDocument();

  const showApprovalDocuments = isApplicationFeePage;
  const showUnselectPaymentModeModal = !isApplicationFeePage;

  const onClickDeleteFile = (file: UploadFile) =>
    isCreateMode
      ? showRemoveFileModal({ file })
      : showRemoveFileModal({
          file,
          onOk: (resolve, reject) =>
            (file as unknown as McApprovalDocumentsType).id
              ? deletePaymentModeDocument(
                  {
                    company_id: parsedID,
                    approval_document_id: (
                      file as unknown as McApprovalDocumentsType
                    ).id,
                  },
                  {
                    onSuccess: () => {
                      resolve();
                      Message.success({
                        content: 'File successfully deleted.',
                      });
                    },
                    onError: reject,
                  },
                )
              : resolve(),
        });

  const paymentModeValues = filterObjectByKey(
    values,
    [approvalDocumentsFieldKey, outletsFieldKey],
    item => {
      const isPaymentMode = !item.includes('_');
      return isPaymentMode && item;
    },
  );

  const fileList = useMemo(
    () =>
      values?.approval_documents?.map(item => {
        const hasFileURL = !!item.file_url;
        const data = {
          ...item,
        };

        if (hasFileURL) {
          data.name = item.file_url.substring(
            item.file_url.lastIndexOf('/') + 1,
          );
        }

        return data;
      }),
    [values.approval_documents],
  );

  const isCheckedAtLeastOne =
    filterObjectByKey(
      values,
      [approvalDocumentsFieldKey, outletsFieldKey],
      item => {
        // application fee values have _ in their key
        const isPaymentMode = !item.includes('_');

        return (
          isPaymentMode &&
          values[item].filter(paymentMode => paymentMode.status).length > 0
        );
      },
    ).length > 0;

  const enableEditInputBoxes = enableUpdateForm;
  const disableFormSubmission = !isCheckedAtLeastOne || !enableUpdateForm;

  useEffect(() => {
    const selectedOutlets = values.outlets;
    if (!isResubmit || !selectedOutlets) return;

    const listExisting = listOutlets.filter(
      outlet =>
        selectedOutlets.includes(`${outlet.id}`) &&
        outlet.has_existing_payment_mode,
    );
    setStatusOutletPaymentMode(
      listExisting.length === selectedOutlets.length
        ? OutletStatusPaymentMode.Existing
        : OutletStatusPaymentMode.New,
    );
  }, [values.outlets, listOutlets, isResubmit, setStatusOutletPaymentMode]);

  return (
    <>
      <div className={style.table}>
        <table>
          <tbody>
            {paymentModeValues.map((item, index) => (
              <React.Fragment key={index}>
                <tr className={style.topRow}>
                  <td colSpan={3} className={style.topCurvedBar} />
                </tr>
                <GroupedInputBoxes
                  key={item}
                  title={item}
                  isFastpay={params.get('merchant_type') === 'fastpay'}
                  paymentModes={values[item]}
                  showUncheckModal={showUnselectPaymentModeModal}
                  disabled={!enableEditInputBoxes}
                  currency={tenantCurrency}
                />
                <tr className={style.bottomRow}>
                  <td colSpan={3} className={style.bottomCurvedBar} />
                </tr>
                <tr className={style.transparentRow}>
                  <td colSpan={3} />
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {showApprovalDocuments && (
        <Input
          name={approvalDocumentsFieldKey}
          type={'file'}
          fileList={fileList}
          onRemove={onClickDeleteFile}
          Text={
            <>
              <p>Click or drag file(s) to this area to upload</p>
              <span className={style.fontBold}>Approval Document</span>
            </>
          }
        />
      )}
      {params.get('merchant_type') !== 'fastpay' && isApplicationFeePage && (
        <div className="mt-3">
          <Select
            name="outlets"
            label="Apply to Outlet"
            placeholder="Select outlets"
            options={listOutlets}
            mode="multiple"
            showSearch
            filterOption={filterOutletOption}
            className={style.outletSelect}
            getPopupContainer={() => document.body}
            listHeight={256}
            listItemHeight={36}
            virtual={false}
          />
        </div>
      )}
      {enableUpdateForm && (
        <div className={style.submitButtonContainer}>
          <SubmitButton
            isDisabled={disableFormSubmission}
            disableIfNotValid={true}
            className={style.submitButton}
            icon={<CaretRight size={16} />}
            iconPlacement={'end'}
          >
            {(_, isSubmitting) => (isSubmitting ? 'Saving...' : 'Save')}
          </SubmitButton>
        </div>
      )}
    </>
  );
};

export default PaymentModeCreateForm;
